// Chat Context

import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import apiClient from '../services/api';
import { ChatMessage, ChatContextType, LoadingStatus } from '../types';

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>('idle');
  const [activeFunctionLabel, setActiveFunctionLabel] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const creatingSession = useRef(false);

  const inferFunctionLabel = (userInput: string): string => {
    const normalized = userInput.toLowerCase();

    if (normalized.includes('pedido')) return 'consultar_pedidos_cliente';
    if (normalized.includes('cotiz')) return 'crear_cotizacion';
    if (normalized.includes('homolog')) return 'consultar_homologos';
    if (normalized.includes('referencia') || normalized.includes('producto') || normalized.includes('precio')) {
      return 'consultar_extensiones_productos';
    }
    if (normalized.includes('nit')) return 'consultar_tercero';

    return 'orquestación del agente';
  };

  const ensureSession = useCallback(async (): Promise<string> => {
    // Already have a session
    if (sessionIdRef.current) return sessionIdRef.current;

    // Avoid creating multiple sessions concurrently
    if (creatingSession.current) {
      // Wait for the ongoing creation to finish
      while (creatingSession.current) {
        await new Promise((r) => setTimeout(r, 50));
      }
      return sessionIdRef.current!;
    }

    creatingSession.current = true;
    try {
      const session = await apiClient.createSession('webchat');
      sessionIdRef.current = session.session_id;
      setSessionId(session.session_id);
      return session.session_id;
    } finally {
      creatingSession.current = false;
    }
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return null;

      // Create and show user message immediately
      const userMessage: ChatMessage = {
        id: `msg_${Date.now()}_user`,
        content,
        sender: 'user',
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setLoadingStatus('thinking');
      setActiveFunctionLabel(inferFunctionLabel(content));

      let currentSessionId: string | null = null;

      try {
        // Ensure session exists (does NOT touch messages)
        currentSessionId = await ensureSession();

        // Simulate function execution phases
        setLoadingStatus('executing');
        await new Promise(resolve => setTimeout(resolve, 800)); // Small delay to show executing status
        
        setLoadingStatus('processing');
        
        // Send to backend
        const updatedSession = await apiClient.sendMessage(currentSessionId, content);

        // Extract assistant's response
        const assistantMessages = updatedSession.messages
          .filter((msg) => msg.role === 'assistant' || msg.role === 'agent')
          .slice(-1);

        if (assistantMessages.length > 0) {
          const botMessage: ChatMessage = {
            id: assistantMessages[0].id,
            content: assistantMessages[0].content,
            sender: 'bot',
            timestamp: assistantMessages[0].timestamp,
            metadata: assistantMessages[0].metadata,
          };
          setMessages((prev) => [...prev, botMessage]);
        }

        return currentSessionId;
      } catch (error) {
        console.error('Failed to send message:', error);

        const errorMessage: ChatMessage = {
          id: `msg_${Date.now()}_error`,
          content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta de nuevo.',
          sender: 'bot',
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, errorMessage]);

        return currentSessionId;
      } finally {
        setIsLoading(false);
        setLoadingStatus('idle');
        setActiveFunctionLabel(null);
      }
    },
    [ensureSession]
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    sessionIdRef.current = null;
    setSessionId(null);
    setLoadingStatus('idle');
    setActiveFunctionLabel(null);
  }, []);

  const loadConversationMessages = useCallback(async (conversationSessionId: string) => {
    try {
      const conversation = await apiClient.getSession(conversationSessionId);
      
      // Convert API messages to ChatMessages
      const chatMessages: ChatMessage[] = conversation.messages.map((msg) => ({
        id: msg.id,
        content: msg.content,
        sender: msg.role === 'user' ? 'user' : 'bot',
        timestamp: msg.timestamp,
        metadata: msg.metadata,
      }));
      
      setMessages(chatMessages);
      sessionIdRef.current = conversationSessionId;
      setSessionId(conversationSessionId);
    } catch (error) {
      console.error('Failed to load conversation messages:', error);
      setMessages([]);
    }
  }, []);

  const value: ChatContextType = {
    messages,
    sendMessage,
    isLoading,
    loadingStatus,
    activeFunctionLabel,
    sessionId,
    clearChat,
    loadConversationMessages,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
